/*!
 * o2pLauncher.js -- Launcher Bar for o2p Touch-Mode Fader Groups
 * Part of oscillaScore control plane
 * (c) 2025 Rob Canning -- GPLv3
 *
 * Creates one launcher bar per o2p group, positioned below the group's
 * bounding box in SVG space. Same DOM structure as controlXY launchers
 * with `o2p-launcher` modifier class for color differentiation.
 *
 * APPROACH: The launcher is embedded in the SVG via <foreignObject>, not
 * as an HTML overlay. This means it automatically tracks the group's
 * position through any SVG transforms (pan, zoom, viewBox changes)
 * without manual repositioning. The trade-off is that coordinate math
 * must map from each path's local space into SVG root user-space
 * (see getGroupBBox). An HTML overlay would avoid coordinate issues
 * but require constant recalculation on scroll/zoom/resize.
 *
 * Persistence: kind "o2pLauncher" via controlShared generic helpers.
 */

import * as shared from "./controlShared.js";
import { onReset } from "../system/teardown.js";

const LAUNCHER_KIND = "o2pLauncher";

// Track which groups already have a launcher
const createdLaunchers = new Set();

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Create a launcher bar for an o2p group.
 * Called from o2p.js after group registration.
 *
 * @param {string} groupId   - The group ID (e.g. "mixerA")
 * @param {SVGRect} bbox     - Bounding box to position below
 * @param {SVGElement} parent - SVG parent to append the foreignObject into
 * @param {Object} [opts]    - Optional overrides
 * @param {number} [opts.slots=8]  - Number of launcher slots
 * @param {number} [opts.banks=4]  - Number of banks
 */
export function createO2PLauncher(groupId, bbox, parent, opts = {}) {
  if (createdLaunchers.has(groupId)) {
    return null;
  }

  const slots = opts.slots ?? 8;
  const totalBanks = opts.banks ?? 4;

  const buttonHeight = 40;   // 32px slots + frame padding: still a comfortable touch target
  const padding = 2;
  const totalHeight = buttonHeight + padding;  // single-row: no bank bar

  // Use parent container bbox for positioning (below the whole mixer, not just faders)
  // repositionLauncherAndCircle works it out again; this is the first place
  const containerBBoxInitial = getParentContainerBBox(groupId) || bbox;
  // Clamp so launcher never overflows the SVG canvas (would be clipped and bank bar invisible)
  const _svgViewH0 = parent?.viewBox?.baseVal?.height || 1024;
  const launcherY = Math.min(
    containerBBoxInitial.y + containerBBoxInitial.height + 2,
    _svgViewH0 - totalHeight - 2
  );

  // -- foreignObject: embeds HTML launcher inside the SVG DOM tree.
  // Placed as a direct child of the SVG root so it shares the root
  // coordinate system. Minimum width prevents button crush in narrow groups.
  const minLauncherWidth = 360;
  const launcherWidth = Math.max(minLauncherWidth, containerBBoxInitial.width);

  const fo = document.createElementNS("http://www.w3.org/2000/svg", "foreignObject");
  fo.setAttribute("x", containerBBoxInitial.x);
  fo.setAttribute("y", launcherY);
  fo.setAttribute("width", launcherWidth);
  fo.setAttribute("height", totalHeight);
  fo.classList.add("controlxy-launcher-container");
  fo.style.overflow = "visible";
  fo.style.display = "none";  // Hidden by default - click orange circle to show

  // -- HTML container --
  const container = document.createElement("div");
  container.className = "controlxy-launcher o2p-launcher";
  container.dataset.uid = groupId;
  container.style.width = "100%";
  container.style.height = "100%";
  container.style.overflow = "visible";
  container.style.boxSizing = "border-box";

  // -- Single button row: preset slots 1–N + settings button --
  const buttonRow = document.createElement("div");
  buttonRow.className = "controlxy-launcher-buttons";

  for (let i = 0; i < slots; i++) {
    const btn = document.createElement("button");
    btn.className = "controlxy-launcher-slot";
    btn.dataset.slot = i;
    btn.innerHTML = `<span class="slot-number">${i + 1}</span><span class="slot-label"></span>`;
    btn.title = "Left-click: Recall | Long-press: Store current";
    buttonRow.appendChild(btn);
  }

  // 9th button — toggles bank bar (second row with bank nav, play/stop, etc.)
  const settingsBtn = document.createElement("button");
  settingsBtn.className = "controlxy-launcher-more-btn";  // distinct class — initializeLauncher skips it
  settingsBtn.title = "Show bank navigation & transport";
  settingsBtn.innerHTML = "&#8943;";  // ⋯ horizontal ellipsis
  // Handler added after toggleBankBar is defined (see below)
  buttonRow.appendChild(settingsBtn);

  container.appendChild(buttonRow);

  // Keep a hidden bank-bar in the DOM so initializeLauncher still finds its elements
  const bankBar = document.createElement("div");
  bankBar.className = "controlxy-launcher-bank-bar";
  bankBar.style.display = "none";
  bankBar.innerHTML = `
    <button class="controlxy-launcher-onoff-btn" data-state="off" title="Recall this group’s on / off preset">off</button>
    <div class="controlxy-launcher-bank-group">
      <button class="controlxy-launcher-bank-btn" data-action="prev" title="Prev bank">&#8592;</button>
      <span class="controlxy-launcher-bank-label"><span class="bank-count">1/${totalBanks}</span></span>
      <button class="controlxy-launcher-bank-btn" data-action="next" title="Next bank">&#8594;</button>
    </div>
    <button class="controlxy-launcher-seq-play" data-action="seq-play" title="Play sequence">&#9654;</button>
    <button class="controlxy-launcher-seq-stop" data-action="seq-stop" title="Stop sequence">&#9632;</button>
    <span class="controlxy-launcher-seq-status"></span>
    <button class="controlxy-launcher-scope-btn" data-scope="user" title="Scope">solo</button>
    <button class="controlxy-launcher-mode-btn" data-mode="preset" title="Mode">presets</button>
    <button class="controlxy-launcher-tween-btn active" data-tween="true" title="Tween">glide</button>
    <button class="controlxy-launcher-settings-btn" title="Open preset manager">manage…</button>
  `;
  container.appendChild(bankBar);

  fo.appendChild(container);
  parent.appendChild(fo);

  createdLaunchers.add(groupId);
  // every window listener of this launcher, aborted by destroyAllO2PLaunchers()
  const ac = new AbortController();
  const signal = ac.signal;

  initializeLauncher(groupId, container, slots, totalBanks, signal);

  // Toggle function for showing/hiding the launcher (preset panel)
  const bankBarHeight = 36;  // second row: 32px buttons + 4px gap

  // Resize + reposition the foreignObject to fit within the SVG canvas
  const resizeFo = (showingBankBar) => {
    // The second row wraps on narrow panels, so measure it (foreignObject
    // content is laid out in SVG user units, the same units as its height).
    const measured = showingBankBar ? Math.ceil(container.scrollHeight) + 2 : 0;
    const newH = Math.max(measured, totalHeight + (showingBankBar ? bankBarHeight + 2 : 0));
    const svgH = fo.ownerSVGElement?.viewBox?.baseVal?.height || 1024;
    const containerBBox = getParentContainerBBox(groupId) || bbox;
    const rawY = containerBBox.y + containerBBox.height + 2;
    fo.setAttribute("height", newH);
    fo.setAttribute("y", Math.min(rawY, svgH - newH - 2));
  };

  // Toggle bank bar (second row)
  let bankBarVisible = false;
  const toggleBankBar = () => {
    bankBarVisible = !bankBarVisible;
    bankBar.style.display = bankBarVisible ? "" : "none";
    settingsBtn.classList.toggle("active", bankBarVisible);
    resizeFo(bankBarVisible);
  };

  // Wire 9th button to toggle bank bar (must come after toggleBankBar is defined)
  settingsBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    toggleBankBar();
  });

  let lastToggleTime = 0;
  const toggleLauncher = (show) => {
    const now = Date.now();
    if (now - lastToggleTime < 100) return;
    lastToggleTime = now;

    if (show === undefined) {
      const visible = fo.style.display !== "none";
      fo.style.display = visible ? "none" : "";
    } else {
      fo.style.display = show ? "" : "none";
    }
    // Reset bank bar when launcher closes
    if (fo.style.display === "none" && bankBarVisible) {
      bankBarVisible = false;
      bankBar.style.display = "none";
      settingsBtn.classList.remove("active");
      resizeFo(false);
    }
  };

  // -- Title bar: an HTML window bar sitting directly ABOVE the panel.
  // Panel name on the left; on the right: fader-value overlays, presets
  // footer, and close (only when the score can reopen the panel).
  // Lives in #scoreContainer for z-order above drag overlays; keeps the
  // o2p-control-bar class that animShared's visibility hook and
  // sequence.js's pointer exclusions look for.
  const scoreContainer = document.getElementById("scoreContainer") || document.body;
  const dragPanelEl = findDragPanel(groupId);

  const controlBar = document.createElement("div");
  controlBar.className = "o2p-control-bar o2p-panel-titlebar";
  controlBar.dataset.uid = groupId;

  const titleEl = document.createElement("span");
  titleEl.className = "o2p-titlebar-name";
  titleEl.textContent = panelTitle(groupId, dragPanelEl);
  controlBar.appendChild(titleEl);

  const btnRow = document.createElement("span");
  btnRow.className = "o2p-titlebar-buttons";
  controlBar.appendChild(btnRow);

  const makeBtn = (cls, html, title) => {
    const b = document.createElement("button");
    b.className = `o2p-control-btn ${cls}`;
    b.innerHTML = html;
    b.title = title;
    b.addEventListener("pointerdown", e => e.stopPropagation());
    btnRow.appendChild(b);
    return b;
  };

  // Fader value overlays for this group (the group's selection state)
  const overlayBtn = makeBtn("o2p-overlay-btn", "values", "Show / hide fader values and touch handles");
  const updateOverlayBtnState = () => {
    overlayBtn.classList.toggle("on", !!window._activeO2PGroups?.has(groupId));
  };
  overlayBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    window._activeO2PGroups = window._activeO2PGroups || new Set();
    const wasActive = window._activeO2PGroups.has(groupId);
    if (wasActive) window._activeO2PGroups.delete(groupId);
    else window._activeO2PGroups.add(groupId);
    updateOverlayBtnState();
    window.dispatchEvent(new CustomEvent('oscilla:group-selected', {
      detail: {
        selected: Array.from(window._activeO2PGroups),
        selectedSet: window._activeO2PGroups,
        groupId: groupId,
        wasActivation: !wasActive,
        wasDeactivation: wasActive
      }
    }));
  });

  // Activate overlay for this group by default on first creation
  window._activeO2PGroups = window._activeO2PGroups || new Set();
  if (!window._activeO2PGroups.has(groupId)) {
    window._activeO2PGroups.add(groupId);
  }
  updateOverlayBtnState();
  window.addEventListener("oscilla:group-selected", () => updateOverlayBtnState(), { signal });

  // Presets footer
  const presetBtn = makeBtn("o2p-preset-btn", "presets", "Show / hide the presets bar under this panel");
  const updatePresetBtnState = () => presetBtn.classList.toggle("on", fo.style.display !== "none");
  presetBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    toggleLauncher();
    updatePresetBtnState();
  });

  // Close — only when something else in the score can bring the panel back
  // (a ui(uid:<panel>, action:toggle) outside it); otherwise a closed panel
  // would be lost until reload.
  const panelUid = dragPanelUid(dragPanelEl);
  if (panelUid && hasExternalToggle(panelUid, dragPanelEl)) {
    const closeBtn = makeBtn("o2p-close-btn", "&times;", "Close panel");
    closeBtn.addEventListener("click", async (e) => {
      e.stopPropagation();
      toggleLauncher(false);
      updatePresetBtnState();
      const { hideElement } = await import('../cues/animShared.js');
      hideElement(panelUid);
      import('../cues/button.js').then(m => m.refreshToggleIndicatorsForUid?.(panelUid));
    });
  }

  scoreContainer.appendChild(controlBar);

  // Store reference for repositioning
  window._o2pControlBars = window._o2pControlBars || {};
  window._o2pControlBars[groupId] = { controlBar, fo, svg: parent, scoreContainer, ac };

  // Reposition after a short delay to ensure transforms are fully applied
  setTimeout(() => {
    repositionLauncherAndCircle(groupId);
  }, 100);

  // Set up MutationObserver to watch for:
  // 1. Transform changes on #scrollStage (score scrolling via translate3d)
  // 2. Transform changes on drag panel (panel dragging)
  const scrollStage = document.getElementById("scrollStage");
  const dragPanel = findDragPanel(groupId);

  const repositionHandler = () => repositionLauncherAndCircle(groupId);

  const observer = new MutationObserver(repositionHandler);

  // Watch scrollStage for score scrolling (CSS transform changes)
  if (scrollStage) {
    observer.observe(scrollStage, { attributes: true, attributeFilter: ["style"] });
  }

  // Watch drag panel for panel dragging (SVG transform changes)
  if (dragPanel) {
    observer.observe(dragPanel, { attributes: true, attributeFilter: ["transform", "style"] });
  }

  // Store observer for cleanup
  window._o2pControlBars[groupId].observer = observer;

  // Hide legacy close buttons in score.svg; the unified control bar replaces them.
  // These have IDs like inputCloseRect, inputClosePath, synthCloseRect, etc.
  setTimeout(() => {
    const closeIds = [
      'inputCloseRect', 'inputClosePath',
      'effectsCloseRect', 'effectsClosePath',
      'chfxCloseRect', 'chfxClosePath',
      'synthCloseRect', 'synthClosePath',
      'polyCtrlCloseRect', 'polyCtrlClosePath',
      'perfGuideCloseRect', 'perfGuideClosePath',
      'polyVisPanelCloseRect', 'polyVisPanelClosePath'
    ];
    for (const id of closeIds) {
      const el = document.getElementById(id);
      if (el) el.style.display = 'none';
    }
  }, 200);

  return fo;
}

/**
 * A project switch: every group's title bar, preset footer, observer and
 * window listeners go, and a group seen again gets a launcher again.
 */
export function destroyAllO2PLaunchers() {
  for (const bar of Object.values(window._o2pControlBars || {})) {
    bar.observer?.disconnect();
    bar.ac?.abort();
    bar.controlBar?.remove();
    bar.fo?.remove();
  }
  window._o2pControlBars = {};
  createdLaunchers.clear();
  for (const g of Object.values(window._o2pTouchGroups || {})) delete g._launcherCreated;
}
onReset("project", destroyAllO2PLaunchers);

// ============================================================================
// REPOSITION (call when new faders register and bbox grows)
// ============================================================================

/**
 * Reposition both the launcher and control bar for a group.
 * Called when the parent group is dragged or when visibility is toggled.
 */
export function repositionLauncherAndCircle(groupId) {
  const bbox = getGroupBBox(groupId);
  if (!bbox) {
    return;
  }

  // Reposition foreignObject launcher (preset panel - below the container)
  const fo = document.querySelector(
    `foreignObject.controlxy-launcher-container .controlxy-launcher.o2p-launcher[data-uid="${groupId}"]`
  )?.closest("foreignObject");

  if (fo) {
    // Footer: flush under the panel, same width (a floor keeps 8 slots usable)
    const containerBBox = getParentContainerBBox(groupId) || bbox;
    const _foH = parseFloat(fo.getAttribute("height")) || 44;
    const _svgH = fo.ownerSVGElement?.viewBox?.baseVal?.height || 1024;
    const _rawY = containerBBox.y + containerBBox.height;
    fo.setAttribute("x", containerBBox.x);
    fo.setAttribute("y", Math.min(_rawY, _svgH - _foH - 2));
    fo.setAttribute("width", Math.max(300, containerBBox.width));
  }

  // Title bar: directly above the panel, full panel width, in screen coords
  // (the panel's own client rect already carries scroll, zoom and drag).
  const barData = window._o2pControlBars?.[groupId];
  if (barData?.controlBar) {
    const bar = barData.controlBar;
    const panel = findDragPanel(groupId);
    const scoreContainer = barData.scoreContainer || document.getElementById("scoreContainer") || document.body;
    const r = panel?.getBoundingClientRect();
    const hidden = !r || (!r.width && !r.height) ||
      getComputedStyle(panel).display === "none" || getComputedStyle(panel).visibility === "hidden";
    if (hidden) {
      bar.style.display = "none";
      return;
    }
    const c = scoreContainer.getBoundingClientRect();
    const barH = bar.offsetHeight || 28;
    const left = r.left - c.left;
    const top  = r.top - c.top - barH;
    const offscreen = left + r.width < 0 || left > c.width;
    bar.style.display = offscreen ? "none" : "flex";
    bar.style.left  = `${left}px`;
    bar.style.top   = `${Math.max(0, top)}px`;
    bar.style.width = `${Math.max(200, r.width)}px`;
  }
}

/**
 * Reposition all o2p launchers and control bars.
 * Called on drag end or resize.
 */
export function repositionAllLaunchers() {
  for (const groupId of Object.keys(window._o2pControlBars || {})) {
    repositionLauncherAndCircle(groupId);
  }
}

// ============================================================================
// COMPUTE GROUP BOUNDING BOX
// ============================================================================

/**
 * Compute a union bounding box of all fader paths in a group,
 * expressed in SVG root user-space coordinates.
 *
 * Why not just getBBox()? Because getBBox() returns coordinates in
 * the element's LOCAL coordinate system, ignoring any transforms on
 * parent <g> elements. Since the launcher foreignObject is a child
 * of the SVG root, we need coordinates in the ROOT's user-space.
 *
 * The transform chain:
 *   pathEl.getScreenCTM()  maps  local coords -> screen pixels
 *   svg.getScreenCTM()     maps  root coords  -> screen pixels
 *   inverse(svg) * pathEl  maps  local coords  -> root coords
 *
 * We transform all 4 corners of each path's local bbox through this
 * matrix, then take the min/max to get the axis-aligned union bbox.
 */
export function getGroupBBox(groupId) {
  const group = window._o2pTouchGroups?.[groupId];
  if (!group) return null;

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const fader of Object.values(group.faders)) {
    const b = bboxInRoot(fader.pathEl);
    if (!b) continue;
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.width);
    maxY = Math.max(maxY, b.y + b.height);
  }

  if (!isFinite(minX)) return null;
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/** One element's bbox in SVG root user-space (the transform chain above), or null. */
function bboxInRoot(el) {
  const svg = el?.ownerSVGElement;
  if (!svg) return null;
  const elCTM = el.getScreenCTM();
  const svgCTM = svg.getScreenCTM();
  if (!elCTM || !svgCTM) return null;

  const b = el.getBBox();
  const toSvgRoot = svgCTM.inverse().multiply(elCTM);
  const pt = svg.createSVGPoint();
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const [x, y] of [[b.x, b.y], [b.x + b.width, b.y], [b.x, b.y + b.height], [b.x + b.width, b.y + b.height]]) {
    pt.x = x;
    pt.y = y;
    const t = pt.matrixTransform(toSvgRoot);
    minX = Math.min(minX, t.x);
    minY = Math.min(minY, t.y);
    maxX = Math.max(maxX, t.x);
    maxY = Math.max(maxY, t.y);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/**
 * Find the drag panel element for a group (used for MutationObserver).
 */
function findDragPanel(groupId) {
  const group = window._o2pTouchGroups?.[groupId];
  if (!group) return null;

  const faders = Object.values(group.faders);
  if (faders.length === 0) return null;

  const pathEl = faders[0].pathEl;
  if (!pathEl) return null;

  const svg = pathEl.ownerSVGElement;
  if (!svg) return null;

  let parentGroup = pathEl.parentElement;
  while (parentGroup && parentGroup !== svg) {
    const id = parentGroup.id || '';
    const dsl = parentGroup.getAttribute?.('data-oscilla') || '';
    if (parentGroup.tagName === 'g' && (id.startsWith('drag(') || id.startsWith('drag_') || dsl.startsWith('drag('))) {
      return parentGroup;
    }
    parentGroup = parentGroup.parentElement;
  }
  return null;
}

// ============================================================================
// PANEL CHROME (title bar + presets footer styling)
// ============================================================================

// uid of the drag() panel: preProcessDrag keeps it in data-oscilla-uid;
// fall back to the DSL text.
function dragPanelUid(panel) {
  if (!panel) return null;
  const kept = panel.getAttribute('data-oscilla-uid');
  if (kept) return kept;
  const dsl = panel.getAttribute('data-oscilla') || panel.id || '';
  return dsl.match(/uid:\s*([\w-]+)/)?.[1] || null;
}

// Title shown in the bar: data-title, else a meaningful Inkscape label, else
// the group id spelled out ("synthLevels" → "Synth levels").
function panelTitle(groupId, panel) {
  const explicit = panel?.getAttribute('data-title');
  if (explicit) return explicit;
  const label = panel?.getAttribute('inkscape:label');
  if (label && !/^(g|layer|group|drag)\b|^(g|layer)\d+$/i.test(label)) return label;
  const words = groupId.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// Is there a ui(uid:<panelUid>, action:toggle) outside the panel itself?
function hasExternalToggle(panelUid, panel) {
  const sel = `[id*="uid:${panelUid}"][id*="action:toggle"], ` +
              `[data-oscilla*="uid:${panelUid}"][data-oscilla*="action:toggle"]`;
  return [...document.querySelectorAll(sel)].some(el => !panel?.contains(el));
}

/**
 * Get the bounding box of the parent container group that encloses the faders.
 * Walks up the DOM from a fader path to find the drag() panel container.
 */
export function getParentContainerBBox(groupId) {
  const group = window._o2pTouchGroups?.[groupId];
  if (!group) return null;

  // Get first fader path element
  const faders = Object.values(group.faders);
  if (faders.length === 0) return null;

  const pathEl = faders[0].pathEl;
  if (!pathEl) return null;

  const svg = pathEl.ownerSVGElement;
  if (!svg) return null;

  // The drag() panel the title bar uses too (by id, renamed or not, or by
  // data-oscilla); else the nearest parent group with an id
  let dragPanel = findDragPanel(groupId);
  if (!dragPanel) {
    let parentGroup = pathEl.parentElement;
    while (parentGroup && parentGroup !== svg) {
      if (parentGroup.tagName === 'g' && parentGroup.id && !parentGroup.id.includes('group:')) {
        dragPanel = parentGroup;
        break;
      }
      parentGroup = parentGroup.parentElement;
    }
  }

  // the faders' own bbox when there is no panel, or it cannot be measured
  return (dragPanel && bboxInRoot(dragPanel)) || getGroupBBox(groupId);
}

// ============================================================================
// INITIALIZATION (state, events, persistence)
// ============================================================================

function initializeLauncher(uid, container, slots, totalBanks, signal) {
  if (!shared.state.initialized) {
    const project = shared.getProjectName();
    if (project && project !== "unknown_project") {
      shared.init(project);
    }
  }

  const state = shared.getOrCreateLauncherByKind(LAUNCHER_KIND, uid, {
    currentBank: 0,
    mode: 'preset',
    tween: true,
    visible: true,
    banks: Array(totalBanks).fill(null).map((_, i) => ({
      name: `Bank ${i + 1}`,
      slots: Array(slots).fill(null)
    }))
  });

  // Ensure enough banks/slots
  while (state.banks.length < totalBanks) {
    state.banks.push({
      name: `Bank ${state.banks.length + 1}`,
      slots: Array(slots).fill(null)
    });
  }
  state.banks.forEach(bank => {
    while ((bank.slots?.length || 0) < slots) {
      bank.slots = bank.slots || [];
      bank.slots.push(null);
    }
  });

  container.dataset.uid = uid;

  function getSequenceList() {
    return window.o2pPresets?.listSequences() || [];
  }

  function saveLauncherState() {
    shared.saveLauncherByKind(LAUNCHER_KIND, uid, state);
  }

  // Sequence status elements
  const seqStatus = container.querySelector('.controlxy-launcher-seq-status');
  const seqPlayBtn = container.querySelector('.controlxy-launcher-seq-play');
  const seqStopBtn = container.querySelector('.controlxy-launcher-seq-stop');

  function updateSeqStatus() {
    const active = window.o2pPresets?.getActiveSequence();
    if (active && active.running) {
      seqStatus.textContent = '\u25B6 ' + active.name;
      seqStatus.classList.add('active');
      seqPlayBtn.classList.add('playing');
      seqStopBtn.classList.add('active');
    } else {
      seqStatus.textContent = '';
      seqStatus.classList.remove('active');
      seqPlayBtn.classList.remove('playing');
      seqStopBtn.classList.remove('active');
    }
  }

  window.addEventListener("o2p:sequenceStarted", updateSeqStatus, { signal });
  window.addEventListener("o2p:sequenceStopped", updateSeqStatus, { signal });
  window.addEventListener("o2p:sequenceStep", updateSeqStatus, { signal });
  window.addEventListener("o2p:sequenceComplete", updateSeqStatus, { signal });

  setTimeout(updateSeqStatus, 100);

  // ---------------------------------------------------------------
  // RENDER BANK
  // ---------------------------------------------------------------

  function renderBank() {
    const bank = state.banks[state.currentBank];
    const bankCount = container.querySelector('.bank-count');
    const buttons = container.querySelectorAll('.controlxy-launcher-slot');
    const modeBtn = container.querySelector('.controlxy-launcher-mode-btn');
    const tweenBtn = container.querySelector('.controlxy-launcher-tween-btn');

    if (state.mode === 'sequence') {
      modeBtn.textContent = 'sequences';
      modeBtn.classList.add('sequence-mode');
      modeBtn.title = 'Sequence Mode (click for Preset mode)';
    } else {
      modeBtn.textContent = 'presets';
      modeBtn.classList.remove('sequence-mode');
      modeBtn.title = 'Preset Mode (click for Sequence mode)';
    }

    if (state.tween) {
      tweenBtn.classList.add('active');
      tweenBtn.title = 'Tween ON (click to Jump)';
    } else {
      tweenBtn.classList.remove('active');
      tweenBtn.title = 'Jump (click for Tween)';
    }

    bankCount.textContent = `${state.currentBank + 1}/${totalBanks}`;

    if (state.mode === 'sequence') {
      const sequences = getSequenceList();
      const startIndex = state.currentBank * slots;

      buttons.forEach((btn, i) => {
        const slotLabel = btn.querySelector('.slot-label');
        const seqIndex = startIndex + i;

        if (seqIndex < sequences.length) {
          const seqName = sequences[seqIndex];
          btn.classList.add('assigned', 'type-sequence');
          btn.classList.remove('empty');
          btn.dataset.type = 'sequence';
          btn.dataset.name = seqName;
          slotLabel.textContent = seqName;
        } else {
          btn.classList.remove('assigned', 'type-sequence');
          btn.classList.add('empty');
          slotLabel.textContent = '';
          delete btn.dataset.type;
          delete btn.dataset.name;
        }
      });
    } else {
      buttons.forEach((btn, i) => {
        const slot = bank.slots[i];
        const slotLabel = btn.querySelector('.slot-label');

        if (slot) {
          btn.classList.add('assigned');
          btn.classList.remove('empty');
          btn.dataset.type = slot.type;
          btn.dataset.name = slot.name;
          slotLabel.textContent = slot.name;

          if (slot.type === 'sequence') {
            btn.classList.add('type-sequence');
          } else {
            btn.classList.remove('type-sequence');
          }
        } else {
          btn.classList.remove('assigned', 'type-sequence');
          btn.classList.add('empty');
          slotLabel.textContent = '';
          delete btn.dataset.type;
          delete btn.dataset.name;
        }
      });
    }

    updateSeqStatus();
  }

  // ---------------------------------------------------------------
  // BANK NAVIGATION
  // ---------------------------------------------------------------

  container.querySelector('[data-action="prev"]').addEventListener('click', (e) => {
    e.stopPropagation();
    state.currentBank = (state.currentBank - 1 + totalBanks) % totalBanks;
    renderBank();
    saveLauncherState();
  });

  container.querySelector('[data-action="next"]').addEventListener('click', (e) => {
    e.stopPropagation();
    state.currentBank = (state.currentBank + 1) % totalBanks;
    renderBank();
    saveLauncherState();
  });

  // ---------------------------------------------------------------
  // SEQUENCE TRANSPORT
  // ---------------------------------------------------------------

  seqPlayBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const sequences = getSequenceList();
    if (sequences.length > 0) {
      const seqName = sequences[0];
      window.o2pPresets?.playSequence(seqName, {
        dur: state.tween ? 1 : 0
      });
    }
    updateSeqStatus();
  });

  seqStopBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    window.o2pPresets?.stopSequence();
    updateSeqStatus();
  });

  // ---------------------------------------------------------------
  // MODE / TWEEN / SETTINGS TOGGLES
  // ---------------------------------------------------------------

  container.querySelector('.controlxy-launcher-mode-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    state.mode = state.mode === 'preset' ? 'sequence' : 'preset';
    renderBank();
    saveLauncherState();
  });

  container.querySelector('.controlxy-launcher-tween-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    state.tween = !state.tween;
    renderBank();
    saveLauncherState();
  });

  container.querySelector('.controlxy-launcher-settings-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    window.o2pPresetUI?.toggle();
  });

  // ---------------------------------------------------------------
  // SCOPE TOGGLE (Solo / Ensemble)
  // ---------------------------------------------------------------
  const scopeBtn = container.querySelector('.controlxy-launcher-scope-btn');
  if (scopeBtn) {
    // Initialize from current scope
    const updateScopeBtn = () => {
      const scope = shared.getPresetScope() || 'composition';
      scopeBtn.dataset.scope = scope;
      scopeBtn.textContent = scope === 'user' ? 'solo' : 'ensemble';
      scopeBtn.title = scope === 'user'
        ? 'Solo mode (local only) - click for Ensemble'
        : 'Ensemble mode (preset sync) - click for Solo';
      scopeBtn.classList.toggle('user-scope', scope === 'user');
    };

    updateScopeBtn();

    scopeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const currentScope = shared.getPresetScope() || 'composition';
      const newScope = currentScope === 'user' ? 'composition' : 'user';
      shared.setPresetScope(newScope);
      updateScopeBtn();
    });

    // Listen for scope changes from preset panel
    window.addEventListener("controlxy:scopeChanged", updateScopeBtn, { signal });
  }

  // ---------------------------------------------------------------
  // ON/OFF TOGGLE BUTTON
  // ---------------------------------------------------------------
  const onoffBtn = container.querySelector('.controlxy-launcher-onoff-btn');
  if (onoffBtn) {
    onoffBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const currentState = onoffBtn.dataset.state;
      const newState = currentState === 'off' ? 'on' : 'off';
      onoffBtn.dataset.state = newState;
      onoffBtn.textContent = newState;
      onoffBtn.classList.toggle('active', newState === 'on');

      // Recall the group-specific preset (e.g., "inputLevels_on", "synthLevels_off")
      const presetName = `${uid}_${newState}`;
      const dur = state.tween ? 0.3 : 0;
      window.o2pPresets?.recall(presetName, { dur, ease: 'easeInOutSine' });
    });
  }

  // ---------------------------------------------------------------
  // SLOT BUTTONS (click to recall, context-menu/long-press to save)
  // ---------------------------------------------------------------

  container.querySelectorAll('.controlxy-launcher-slot').forEach(btn => {
    // Click to recall
    btn.addEventListener('click', (e) => {
      e.stopPropagation();

      const name = btn.dataset.name;
      const type = btn.dataset.type;
      if (!name) return;

      container.querySelectorAll('.controlxy-launcher-slot').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');

      const dur = state.tween ? 1 : 0;

      if (type === 'preset') {
        window.o2pPresets?.recall(name, { dur, ease: 'easeInOutSine' });
      } else if (type === 'sequence') {
        window.o2pPresets?.playSequence(name, { dur });
      }

      setTimeout(() => btn.classList.remove('active'), 300);
      updateSeqStatus();
    });

    // Right-click to store current state
    btn.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const slotIndex = parseInt(btn.dataset.slot);
      const slotName = prompt('Save current state as:', `${uid}_${state.currentBank + 1}_${slotIndex + 1}`);
      if (!slotName) return;

      // Pass uid as groupFilter to only save THIS group's faders
      window.o2pPresets?.save(slotName, uid);

      state.banks[state.currentBank].slots[slotIndex] = {
        type: 'preset',
        name: slotName
      };

      renderBank();
      saveLauncherState();
    });

    // Long-press triggers context menu
    let pressTimer;
    btn.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      pressTimer = setTimeout(() => {
        btn.dispatchEvent(new MouseEvent('contextmenu', {
          bubbles: true, cancelable: true, view: window
        }));
      }, 500);
    });
    btn.addEventListener('pointerup', () => clearTimeout(pressTimer));
    btn.addEventListener('pointercancel', () => clearTimeout(pressTimer));
  });

  renderBank();

  // ---------------------------------------------------------------
  // REFRESH HANDLERS (persistence reload)
  // ---------------------------------------------------------------

  const refreshHandler = (event) => {
    const { uid: targetUid } = event.detail || {};
    if (!targetUid || targetUid === uid) {
      const launcher = shared.getOrCreateLauncherByKind(LAUNCHER_KIND, uid, state);
      if (launcher) {
        state.currentBank = launcher.currentBank ?? state.currentBank;
        state.mode = launcher.mode ?? state.mode;
        state.tween = launcher.tween ?? state.tween;
        state.visible = launcher.visible ?? state.visible;
        if (launcher.banks) state.banks = launcher.banks;
      }
      renderBank();
    }
  };

  window.addEventListener("o2p:launcherRefresh", refreshHandler, { signal });

  const loadedHandler = () => {
    const launcher = shared.getOrCreateLauncherByKind(LAUNCHER_KIND, uid, state);
    if (launcher) {
      state.currentBank = launcher.currentBank ?? state.currentBank;
      state.mode = launcher.mode ?? state.mode;
      state.tween = launcher.tween ?? state.tween;
      state.visible = launcher.visible ?? state.visible;
      if (launcher.banks && launcher.banks.length > 0) {
        state.banks = launcher.banks;
        state.banks.forEach(bank => {
          while ((bank.slots?.length || 0) < slots) {
            bank.slots = bank.slots || [];
            bank.slots.push(null);
          }
        });
      }
      renderBank();
    }
  };

  window.addEventListener("controlxy:loaded", loadedHandler, { signal });
  window.addEventListener("controlxy:serverLoaded", loadedHandler, { signal });
}

